import { CalendarIcon, X } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useLanguage, useTranslation } from '@/hooks/use-translation'

interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  id?: string
  label?: string
  allowClear?: boolean
  clearLabel?: string
}

/**
 * DatePicker component extracted from DateRangeFilter.
 * Simple calendar picker with button trigger.
 */
export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  minDate,
  maxDate,
  className,
  id,
  label,
  allowClear = false,
  clearLabel,
}: DatePickerProps) {
  const t = useTranslation()
  const { currentLanguage } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)

  const defaultMinDate = new Date(1900, 0, 1)
  const defaultMaxDate = new Date()

  const effectiveMinDate = minDate || defaultMinDate
  const effectiveMaxDate = maxDate || defaultMaxDate

  const displayPlaceholder = placeholder || t('common.datePicker.placeholder')

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(date)
    if (date) {
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    onChange?.(undefined)
  }

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="relative">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              variant="outline"
              className="w-full justify-start text-left font-normal h-9 bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              disabled={disabled}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? value.toLocaleDateString(currentLanguage) : displayPlaceholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={value}
              onSelect={handleDateSelect}
              captionLayout="dropdown"
              autoFocus
              defaultMonth={value || undefined}
              startMonth={effectiveMinDate}
              endMonth={effectiveMaxDate}
              showOutsideDays={true}
              disabled={(date) => {
                if (date < effectiveMinDate) return true
                if (date > effectiveMaxDate) return true
                return false
              }}
              classNames={{
                day: 'h-9 w-9 text-sm',
              }}
            />
          </PopoverContent>
        </Popover>

        {allowClear && value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1 h-7 w-7 p-0 hover:bg-gray-100"
            onClick={handleClear}
            disabled={disabled}
            title={clearLabel || 'Clear date'}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  )
}
